import { Module } from 'vuex';
import type { AxiosRequestConfig } from 'axios';
import api from '@/services/api';
import {
  DEFAULT_ADMIN_THEME,
  isAdminThemeKey,
  type AdminThemeKey,
} from '@/support/adminThemes';
import type { User } from './auth';

export interface GeneralSettings {
  show_cashier_client_card: boolean;
  cash_drawer_enabled: boolean;
}

export interface AppearanceSettings {
  admin_theme: AdminThemeKey;
}

export interface PrintingSettings {
  thermal_enabled: boolean;
  fallback_browser: boolean;
  auto_print_after_sale: boolean;
}

export interface InventorySettings {
  hide_processed_products: boolean;
  show_one_rayon_at_time: boolean;
}

export interface TutorialSettings {
  auto_start: boolean;
  seen_tours: Record<string, number>;
  seen_zones: Record<string, number>;
}

export interface NoticeSettings {
  shop_limit_reached_seen: boolean;
  release_notes_acknowledged_version: string;
}

export interface UserSettings {
  general: GeneralSettings;
  appearance: AppearanceSettings;
  printing: PrintingSettings;
  inventory: InventorySettings;
  tutorial: TutorialSettings;
  notices: NoticeSettings;
}

export interface UserSettingsState {
  settings: UserSettings;
  isLoading: boolean;
  isSaving: boolean;
  lastLoadedUserId: number | null;
}

interface SaveSettingsPayload {
  settings: Partial<UserSettings>;
  silent?: boolean;
}

interface SilentToastConfig {
  skipSuccessToast: boolean;
}

export const USER_SETTINGS_STORAGE_PREFIX = 'suite241_gestion_commerciale_user_settings_';

export const DEFAULT_USER_SETTINGS: UserSettings = {
  general: {
    show_cashier_client_card: true,
    cash_drawer_enabled: false,
  },
  appearance: {
    admin_theme: DEFAULT_ADMIN_THEME,
  },
  printing: {
    thermal_enabled: false,
    fallback_browser: true,
    auto_print_after_sale: true,
  },
  inventory: {
    hide_processed_products: true,
    show_one_rayon_at_time: false,
  },
  tutorial: {
    auto_start: true,
    seen_tours: {},
    seen_zones: {},
  },
  notices: {
    shop_limit_reached_seen: false,
    release_notes_acknowledged_version: '',
  },
};

const cloneDefaults = (): UserSettings => JSON.parse(JSON.stringify(DEFAULT_USER_SETTINGS));

const toBoolean = (value: unknown, fallback: boolean): boolean => {
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === '1') return true;
  if (value === 0 || value === '0') return false;
  if (typeof value === 'string') {
    const normalized = value.toLowerCase().trim();
    if (['true', 'yes', 'on'].includes(normalized)) return true;
    if (['false', 'no', 'off'].includes(normalized)) return false;
  }

  return fallback;
};

const normalizeAdminTheme = (value: unknown, fallback: AdminThemeKey): AdminThemeKey => {
  return isAdminThemeKey(value) ? value : fallback;
};

const normalizeVersion = (value: unknown): string => {
  if (typeof value !== 'string') return '';

  const version = value.trim();

  return /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/.test(version) ? version : '';
};

const normalizeSeenTours = (value: unknown): Record<string, number> => {
  if (!value || typeof value !== 'object') return {};

  return Object.entries(value as Record<string, unknown>).reduce<Record<string, number>>((seenTours, [key, version]) => {
    const normalizedVersion = Number(version);
    if (!/^[A-Za-z0-9_.:-]+$/.test(key) || !Number.isFinite(normalizedVersion) || normalizedVersion < 1) {
      return seenTours;
    }

    seenTours[key] = Math.floor(normalizedVersion);
    return seenTours;
  }, {});
};

const normalizeSettings = (value: unknown): UserSettings => {
  const raw = (value && typeof value === 'object' ? value : {}) as Partial<UserSettings>;
  const defaults = cloneDefaults();

  return {
    general: {
      show_cashier_client_card: toBoolean(
        raw.general?.show_cashier_client_card,
        defaults.general.show_cashier_client_card
      ),
      cash_drawer_enabled: toBoolean(
        raw.general?.cash_drawer_enabled,
        defaults.general.cash_drawer_enabled
      ),
    },
    appearance: {
      admin_theme: normalizeAdminTheme(
        raw.appearance?.admin_theme,
        defaults.appearance.admin_theme
      ),
    },
    printing: {
      thermal_enabled: toBoolean(
        raw.printing?.thermal_enabled,
        defaults.printing.thermal_enabled
      ),
      fallback_browser: toBoolean(
        raw.printing?.fallback_browser,
        defaults.printing.fallback_browser
      ),
      auto_print_after_sale: toBoolean(
        raw.printing?.auto_print_after_sale,
        defaults.printing.auto_print_after_sale
      ),
    },
    inventory: {
      hide_processed_products: toBoolean(
        raw.inventory?.hide_processed_products,
        defaults.inventory.hide_processed_products
      ),
      show_one_rayon_at_time: toBoolean(
        raw.inventory?.show_one_rayon_at_time,
        defaults.inventory.show_one_rayon_at_time
      ),
    },
    tutorial: {
      auto_start: toBoolean(
        raw.tutorial?.auto_start,
        defaults.tutorial.auto_start
      ),
      seen_tours: normalizeSeenTours(raw.tutorial?.seen_tours),
      seen_zones: normalizeSeenTours(raw.tutorial?.seen_zones),
    },
    notices: {
      shop_limit_reached_seen: toBoolean(
        raw.notices?.shop_limit_reached_seen,
        defaults.notices.shop_limit_reached_seen
      ),
      release_notes_acknowledged_version: normalizeVersion(
        raw.notices?.release_notes_acknowledged_version
      ),
    },
  };
};

const cacheKeyForUser = (user: User | null | undefined): string | null => {
  return user?.id ? `${USER_SETTINGS_STORAGE_PREFIX}${user.id}` : null;
};

const readCachedSettings = (user: User | null | undefined): UserSettings | null => {
  const key = cacheKeyForUser(user);
  if (!key || typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(key);
    return raw ? normalizeSettings(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
};

const writeCachedSettings = (user: User | null | undefined, settings: UserSettings): void => {
  const key = cacheKeyForUser(user);
  if (!key || typeof window === 'undefined') return;

  window.localStorage.setItem(key, JSON.stringify(settings));
};

const userSettingsModule: Module<UserSettingsState, Record<string, unknown>> = {
  namespaced: true,

  state: (): UserSettingsState => ({
    settings: cloneDefaults(),
    isLoading: false,
    isSaving: false,
    lastLoadedUserId: null,
  }),

  getters: {
    settings(state): UserSettings {
      return state.settings;
    },
    general(state): GeneralSettings {
      return state.settings.general;
    },
    printing(state): PrintingSettings {
      return state.settings.printing;
    },
    appearance(state): AppearanceSettings {
      return state.settings.appearance;
    },
    inventory(state): InventorySettings {
      return state.settings.inventory;
    },
    tutorial(state): TutorialSettings {
      return state.settings.tutorial;
    },
    isLoading(state): boolean {
      return state.isLoading;
    },
    isSaving(state): boolean {
      return state.isSaving;
    },
  },

  mutations: {
    SET_SETTINGS(state, settings: UserSettings) {
      state.settings = normalizeSettings(settings);
    },
    SET_LOADING(state, value: boolean) {
      state.isLoading = value;
    },
    SET_SAVING(state, value: boolean) {
      state.isSaving = value;
    },
    SET_LAST_LOADED_USER_ID(state, userId: number | null) {
      state.lastLoadedUserId = userId;
    },
    RESET(state) {
      state.settings = cloneDefaults();
      state.isLoading = false;
      state.isSaving = false;
      state.lastLoadedUserId = null;
    },
  },

  actions: {
    LOAD_FROM_LOCALSTORAGE({ commit, rootGetters }) {
      const user = rootGetters['auth/currentUser'] as User | null;
      const cached = readCachedSettings(user);

      if (cached) {
        commit('SET_SETTINGS', cached);
        commit('SET_LAST_LOADED_USER_ID', user?.id || null);
      }
    },

    async load({ commit, rootGetters, state }, options: { silent?: boolean } | null = {}) {
      const user = rootGetters['auth/currentUser'] as User | null;
      if (!user) {
        commit('RESET');
        return cloneDefaults();
      }

      const cached = readCachedSettings(user);
      if (cached && state.lastLoadedUserId !== user.id) {
        commit('SET_SETTINGS', cached);
      }

      commit('SET_LOADING', true);
      try {
        const requestConfig: AxiosRequestConfig | undefined = options?.silent
          ? { skipErrorToast: true } as AxiosRequestConfig
          : undefined;
        const response = await api.get('/user-settings', requestConfig);
        const settings = normalizeSettings(response.data?.data || response.data);
        commit('SET_SETTINGS', settings);
        commit('SET_LAST_LOADED_USER_ID', user.id);
        writeCachedSettings(user, settings);

        return settings;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async save({ commit, rootGetters }, payload: Partial<UserSettings> | SaveSettingsPayload) {
      const user = rootGetters['auth/currentUser'] as User | null;
      const input = payload && typeof payload === 'object' && 'settings' in payload
        ? payload.settings
        : payload;
      const isSilent = payload && typeof payload === 'object' && 'silent' in payload
        ? Boolean(payload.silent)
        : false;
      const normalized = normalizeSettings(input);

      commit('SET_SAVING', true);
      try {
        const silentToastConfig: AxiosRequestConfig<UserSettings> & SilentToastConfig = { skipSuccessToast: isSilent };
        const response = await api.put('/user-settings', normalized, silentToastConfig);
        const saved = normalizeSettings(response.data?.data || response.data);
        commit('SET_SETTINGS', saved);
        commit('SET_LAST_LOADED_USER_ID', user?.id || null);
        writeCachedSettings(user, saved);

        return saved;
      } finally {
        commit('SET_SAVING', false);
      }
    },

    clear({ commit }) {
      commit('RESET');
    },
  },
};

export default userSettingsModule;
